// @flow strict
import invariant from 'invariant';

import type {SchedulerAvailability, TimeSpan} from 'src/types/scheduler';

import {values} from 'src/utils/object';

import useNoStoreApi from 'src/hooks/useNoStoreApi';
import {useApi} from 'src/hooks/useApi';


const timeAsMins = (str: string) => {
  const [h, m] = str.split(':').map((part) => parseInt(part, 10));
  return h * 60 + m;
};

export const useTemplateAvailability = ({
  templateId,
  timezone,
  eventId,
  isPreview = false,
  extraOptions,
  onError,
}: {
  templateId?: string,
  timezone?: string,
  eventId?: string,
  isPreview?: boolean,
  extraOptions?: {[key: string]: mixed},
  onError?: (Error) => mixed,
}): ({
  eventDates: Date[],
  availabilityByDate: (key: string | Date) => TimeSpan[],
  isAvailabilityLoading: boolean,
  hasAvailability: boolean,
  eventCount: number,
  availability: {
    [date: string]: TimeSpan[],
  },
  error: null | Error,
}) => {
  const viewer_timezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const templateResource = useNoStoreApi<SchedulerAvailability>(
    templateId
      ? eventId
        ? {
            pathname: `scheduler/event-template/${templateId}/availability${
              isPreview ? '-preview' : ''
            }`,
            query: viewer_timezone
              ? {event_id: eventId, viewer_timezone}
              : {event_id: eventId},
          }
        : {
            pathname: `scheduler/event-template/${templateId}/availability${
              isPreview ? '-preview' : ''
            }`,
            query: viewer_timezone
              ? extraOptions
                ? // $FlowIssue[cannot-spread-indexer]
                  {viewer_timezone, ...extraOptions}
                : {viewer_timezone}
              : undefined,
          }
      : null,
    isPreview ? {method: 'post', onError} : undefined,
  );

  const eventDateKeys = Object.keys(
    templateResource.result?.free_time_slots ?? {},
  );

  const availability = templateResource.result?.free_time_slots ?? {};

  const eventDates = eventDateKeys
    .map((str) => {
      const [y, m, d] = str.split('-').map((n) => parseInt(n, 10));
      return new Date(y, m - 1, d);
    })
    .sort((l, r) => l - r);

  const eventCount = eventDateKeys.reduce((acc, currDate) => {
    return (
      acc + (templateResource.result?.free_time_slots[currDate] ?? []).length
    );
  }, 0);

  return {
    eventDates,
    availabilityByDate: (dateKey: string | Date) => {
      let key = dateKey;
      if (dateKey instanceof Date) {
        key = [
          dateKey.getFullYear(),
          `${dateKey.getMonth() + 1}`.padStart(2, '0'),
          `${dateKey.getDate()}`.padStart(2, '0'),
        ].join('-');
      }
      invariant(typeof key === 'string', 'dateKey must be string or Date');
      return (templateResource.result?.free_time_slots[key] ?? []).sort(
        (left, right) => {
          return timeAsMins(left.start_time) - timeAsMins(right.start_time);
        },
      );
    },
    // NOTE (kyle): because we don't make the request initially, we have to fudge this boolean
    isAvailabilityLoading: !templateResource.result && !templateResource.error,
    hasAvailability: Boolean(
      templateResource.result &&
        values(templateResource.result.free_time_slots).some(
          (slots) => slots.length > 0,
        ),
    ),
    eventCount,
    availability,
    error: templateResource.error,
  };
};

export const useTemplateAvailabilityPreview = (
  templateId?: string,
  timezone?: string,
  eventId?: string,
  extraOptions?: {[key: string]: mixed},
): ({
  eventDates: Date[],
  availabilityByDate: (key: string | Date) => TimeSpan[],
  isAvailabilityLoading: boolean,
  hasAvailability: boolean,
  eventCount: number,
  error: null | Error,
  refetch: () => mixed,
}) => {
  const viewer_timezone =
    timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const templateResource = useApi<SchedulerAvailability>(
    templateId
      ? {
          pathname: `scheduler/event-template/${templateId}/availability-preview`,
        }
      : null,
    {
      method: 'POST',
      // $FlowFixMe[cannot-spread-indexer] not sure what the best approach is
      data: {viewer_timezone, ...extraOptions},
      deps: [extraOptions ? JSON.stringify(extraOptions) : ''],
    },
  );

  const eventDateKeys = Object.keys(
    templateResource.result?.free_time_slots ?? {},
  );

  const eventDates = eventDateKeys
    .map((str) => {
      const [y, m, d] = str.split('-').map((n) => parseInt(n, 10));
      return new Date(y, m - 1, d);
    })
    .sort((l, r) => l - r);

  const eventCount = eventDateKeys.reduce((acc, currDate) => {
    return (
      acc + (templateResource.result?.free_time_slots[currDate] ?? []).length
    );
  }, 0);

  return {
    error: templateResource.error,
    eventDates,
    refetch: templateResource.refetch,
    availabilityByDate: (dateKey: string | Date) => {
      let key = dateKey;
      if (dateKey instanceof Date) {
        key = [
          dateKey.getFullYear(),
          `${dateKey.getMonth() + 1}`.padStart(2, '0'),
          `${dateKey.getDate()}`.padStart(2, '0'),
        ].join('-');
      }
      invariant(typeof key === 'string', 'dateKey must be string or Date');
      return (templateResource.result?.free_time_slots[key] ?? []).sort(
        (left, right) => {
          return timeAsMins(left.start_time) - timeAsMins(right.start_time);
        },
      );
    },
    // NOTE (kyle): because we don't make the request initially, we have to fudge this boolean
    isAvailabilityLoading: templateResource.isLoading, //!templateResource.result && !templateResource.error,
    hasAvailability: Boolean(
      templateResource.result &&
        values(templateResource.result.free_time_slots).some(
          (slots) => slots.length > 0,
        ),
    ),
    eventCount,
  };
};
